/**
 * Token Cost Modal Component
 * Reusable modal for operations that cost tokens
 * Shows token cost, wallet balance, and handles insufficient balance
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import { useAuthStore } from '../lib/auth';
import { useWalletBalance } from '../lib/useWalletBalance';

interface TokenCostModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  description: string;
  tokenCost: number;
  confirmButtonText?: string;
  estimatedDuration?: string;
}

export default function TokenCostModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  tokenCost,
  confirmButtonText = 'Start Extraction',
  estimatedDuration = '20-30 seconden',
}: TokenCostModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspace } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);

  // Use wallet balance hook with fetch on mount
  const { balance: walletBalance, refresh: refreshBalance } = useWalletBalance({
    fetchOnMount: true,
  });

  const handleConfirm = async () => {
    // Check if user has enough tokens
    if (walletBalance < tokenCost) {
      onClose();
      // Redirect to billing page
      setTimeout(() => {
        if (workspace?.slug) {
          navigate(`/${workspace.slug}/settings/billing`);
        }
      }, 100);
      return;
    }

    setIsProcessing(true);
    try {
      await onConfirm();

      // Refresh wallet balance after successful operation
      console.log('[TOKEN_MODAL] Operation completed, refreshing balance...');
      await refreshBalance();

      onClose();
    } catch (error) {
      console.error('Operation failed:', error);
      // Don't close modal on error so user can see error message
    } finally {
      setIsProcessing(false);
    }
  };

  const hasInsufficientBalance = walletBalance < tokenCost;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={() => {
        // Prevent closing during processing
        if (!isProcessing) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in-0" />
        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-full max-w-lg
            bg-white dark:bg-gray-900
            border-2 border-black dark:border-white
            rounded-lg shadow-brutal
            p-6
            z-50
            animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2
          "
        >
          <Dialog.Title className="text-2xl font-bold mb-2 dark:text-white">
            {title}
          </Dialog.Title>

          <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {description}
          </Dialog.Description>

          <div className="space-y-4 mb-6">
            {/* Token Cost */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium dark:text-white">{t('tokenCostModal.tokenCost')}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('tokenCostModal.estimatedUsage')}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold dark:text-white">
                  {tokenCost.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('common.tokens')}</p>
              </div>
            </div>

            {/* Wallet Balance */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium dark:text-white">{t('tokenCostModal.yourBalance')}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('tokenCostModal.availableTokens')}</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${
                  hasInsufficientBalance
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  {walletBalance.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('common.tokens')}</p>
              </div>
            </div>

            {/* Warning if insufficient balance */}
            {hasInsufficientBalance && (
              <div className="p-3 border-2 border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-200 text-sm rounded-lg">
                <p className="font-medium mb-1">{t('tokenCostModal.insufficientTokens')}</p>
                <p>{t('tokenCostModal.insufficientTokensMessage')}</p>
              </div>
            )}

            {/* Duration info */}
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p>{t('tokenCostModal.estimatedDuration', { duration: estimatedDuration })}</p>
              <p>{t('tokenCostModal.autoUpdate')}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {hasInsufficientBalance ? (
              <>
                <Button
                  onClick={onClose}
                  variant="secondary"
                  className="flex-1"
                >
                  {t('tokenCostModal.cancel')}
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    if (workspace?.slug) {
                      navigate(`/${workspace.slug}/settings/billing`);
                    }
                  }}
                  className="flex-1"
                >
                  {t('tokenCostModal.topUp')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={onClose}
                  variant="secondary"
                  className="flex-1"
                  disabled={isProcessing}
                >
                  {t('tokenCostModal.cancel')}
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1"
                  disabled={isProcessing}
                >
                  {isProcessing ? t('tokenCostModal.processing') : confirmButtonText}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
